import { AuctionTypeEnum } from '../../constants';
import type { AuctionConfigurationErrors } from './errors';
import type {
  AuctionConfigurationFields,
  AuctionConfigurationMintQuantityLimits,
  AuctionConfigurationPriceLimits,
  AuctionConfigurationState,
  AuctionConfigurationTouchedFields,
} from './schema';

export function initialAuctionConfigurationErrors() {
  return {
    maxMintable: null,
    maxQuantity: null,
    minQuantity: null,
    presaleMaxMintsPerWallet: null,
    presaleMaxSupply: null,
    presalePricePerMint: null,
    publicMaxMintsPerWallet: null,
    publicPricePerMint: null,
    presaleStartTime: null,
    publicStartTime: null,
    presaleEndTime: null,
    publicEndTime: null,
    publicCutoffTime: null,
    presaleAllowList: {
      addresses: null,
      description: null,
    },
  } satisfies AuctionConfigurationErrors;
}

export function initialAuctionConfigurationFields() {
  return {
    auctionType: AuctionTypeEnum.OPEN_EDITION,
    maxMintable: null,
    maxQuantity: null,
    minQuantity: null,
    presaleMaxMintsPerWallet: null,
    presaleMaxSupply: null,
    presalePricePerMint: null,
    presaleStartTime: null,
    presaleEndTime: null,
    publicEndTime: null,
    publicCutoffTime: null,
    publicMaxMintsPerWallet: null,
    publicPricePerMint: null,
    publicStartTime: null,
    presaleAllowList: {
      addresses: [],
      description: null,
      collectorsOfReleases: {
        selectedReleaseIds: [],
        allArtistCollaborations: false,
      },
      collectorsOfArtists: {
        allArtistCollectors: true,
        selectedArtistIds: [],
        allCollaboratingArtists: false,
      },
    },
  } satisfies AuctionConfigurationFields;
}

export function initialAuctionConfigurationPriceLimits() {
  return {
    presaleMaxPrice: undefined,
    presaleMinPrice: undefined,
    publicMaxPrice: undefined,
    publicMinPrice: undefined,
  } satisfies AuctionConfigurationPriceLimits;
}

export function initialAuctionConfigurationMintQuantityLimits() {
  return {
    minQuantity: undefined,
    maxQuantity: undefined,
    minPublicSaleMaxMintsPerWallet: undefined,
    maxPublicSaleMaxMintsPerWallet: undefined,
    minPresaleMaxMintsPerWallet: undefined,
    maxPresaleMaxMintsPerWallet: undefined,
  } satisfies AuctionConfigurationMintQuantityLimits;
}

export const auctionConfigurationDirtyFields = {
  auctionType: true,
  maxMintable: true,
  maxQuantity: true,
  minQuantity: true,
  presaleMaxMintsPerWallet: true,
  presaleMaxSupply: true,
  presalePricePerMint: true,
  presaleStartTime: true,
  presaleEndTime: true,
  publicEndTime: true,
  publicCutoffTime: true,
  publicMaxMintsPerWallet: true,
  publicPricePerMint: true,
  publicStartTime: true,
  presaleAllowList: {
    description: true,
    addresses: true,
    collectorsOfReleases: true,
    collectorsOfArtists: true,
  },
} satisfies AuctionConfigurationTouchedFields;

export function initialAuctionConfigurationTouchedFields() {
  return {
    auctionType: false,
    maxMintable: false,
    maxQuantity: false,
    minQuantity: false,
    presaleMaxMintsPerWallet: false,
    presaleMaxSupply: false,
    presalePricePerMint: false,
    presaleStartTime: false,
    presaleEndTime: false,
    publicEndTime: false,
    publicCutoffTime: false,
    publicMaxMintsPerWallet: false,
    publicPricePerMint: false,
    publicStartTime: false,
    presaleAllowList: {
      description: false,
      addresses: false,
      collectorsOfReleases: false,
      collectorsOfArtists: false,
    },
  } satisfies AuctionConfigurationTouchedFields;
}

export function auctionConfigInitialState() {
  return {
    errors: initialAuctionConfigurationErrors(),
    fields: initialAuctionConfigurationFields(),
    mintQuantityLimit: initialAuctionConfigurationMintQuantityLimits(),
    priceLimit: initialAuctionConfigurationPriceLimits(),
    touched: initialAuctionConfigurationTouchedFields(),
    hasPresaleMint: false,
    hasGAEdition: true,
    isValid: false,
  } satisfies AuctionConfigurationState;
}
